import { OfferDiscountType, type UsableOfferDto } from "../api/hooks/useOffers";
import type { StrategyCartItem } from "../state/cartStore";

/**
 * What an offer takes off a line's first bill.
 *
 * Worked out here for now so the cart can show a number while the customer chooses. It is the same
 * arithmetic Stripe does, but it is not Stripe's answer: proration, an existing discount or a price
 * change would all make it differ. Before checkout applies anything, the figure shown comes from
 * Stripe's own preview (WI #794) and this becomes the optimistic value shown until it returns.
 */
export function savingOnLine(offer: UsableOfferDto, priceUsd: number): number {
  const raw =
    offer.discountType === OfferDiscountType.Percent
      ? (priceUsd * offer.discountValue) / 100
      : offer.discountValue;

  // A dollar offer worth more than the line cannot take the price below nothing. Checkout refuses
  // that case outright rather than billing a negative; the cart just shows what it would save.
  return Math.min(Math.round(raw * 100) / 100, priceUsd);
}

/** What the offer chosen for a cart line takes off it, or nothing when it has none. */
export function savingOnItem(item: StrategyCartItem, offers: UsableOfferDto[]): number {
  const offer = offers.find((o) => o.id === item.offerId);
  return offer ? savingOnLine(offer, item.priceUsd) : 0;
}

/** The lines this offer is already on, newest choice last. */
export function linesUsing(items: StrategyCartItem[], offerId: string): StrategyCartItem[] {
  return items.filter((item) => item.offerId === offerId);
}

/**
 * Which lines an offer has to come off before it can go on another, and in which order.
 *
 * It comes off the ones where it saves least, so a customer who spends a one-strategy offer
 * somewhere new loses the smallest amount they could have. Returns nothing when the offer has room.
 */
export function linesToFree(
  items: StrategyCartItem[],
  offer: UsableOfferDto,
  targetStrategyId: string
): StrategyCartItem[] {
  if (offer.maxStrategies === null) {
    return [];
  }

  const inUse = linesUsing(items, offer.id).filter(
    (item) => item.strategyId !== targetStrategyId
  );

  const overBy = inUse.length + 1 - offer.maxStrategies;
  if (overBy <= 0) {
    return [];
  }

  return [...inUse]
    .sort((a, b) => savingOnLine(offer, a.priceUsd) - savingOnLine(offer, b.priceUsd))
    .slice(0, overBy);
}
